import React, { useCallback, useEffect, useMemo, useState } from "react";
import { format, startOfMonth } from "date-fns";
import { id } from "date-fns/locale";
import { connect } from "react-redux";
import {
  fetchCurrentStock,
  fetchStockMaterialConsumption,
  fetchStockMaterialUsage,
} from "../../actions/stockReportAction";
import ItemAvatar from "../../components/ItemAvatar";
import AkasSelect from "../../components/AkasSelect";
import MaterialUsageChart from "../DashboardPage/components/MaterialUsageChart";
import useAkasChartColors from "../../hooks/useAkasChartColors";

const today = format(new Date(), "yyyy-MM-dd");
const monthStart = format(startOfMonth(new Date()), "yyyy-MM-dd");

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("id-ID", {
    maximumFractionDigits: 2,
  });

const formatQty = (value, unit) => `${formatNumber(value)} ${unit || ""}`.trim();

const formatDateLabel = (value) => {
  if (!value) return "—";
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? "—" : format(date, "dd MMM yyyy", { locale: id });
};

const StockReportIcon = ({ name, className = "icon icon-1" }) => {
  const icons = {
    search: (
      <>
        <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
        <path d="M21 21l-6 -6" />
      </>
    ),
    stock: (
      <>
        <path d="M12 3l8 4.5v9l-8 4.5l-8 -4.5v-9z" />
        <path d="M12 12l8 -4.5M12 12v9M12 12l-8 -4.5" />
      </>
    ),
  };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}>
      {icons[name] || icons.stock}
    </svg>
  );
};

const SummaryCard = ({ title, value, subtitle, color = "primary" }) => (
  <div className="col-6 col-xl-3">
    <div className="card card-sm h-100 border-0 shadow-sm akas-summary-card">
      <div className="card-body">
        <div className="d-flex align-items-center">
          <span className={`avatar bg-${color}-lt text-${color} me-3`}>
            <StockReportIcon name="stock" />
          </span>
          <div className="flex-fill">
            <div className="text-secondary fw-bold text-uppercase small">{title}</div>
            <div className={`h2 mb-0 mt-1 text-${color}`}>{value}</div>
            <div className="text-secondary small mt-1">{subtitle}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const TableSkeletonRows = ({ rows = 6 }) => (
  <>
    {[...Array(rows)].map((_, rowIndex) => (
      <tr key={`stock-report-skeleton-${rowIndex}`} style={{ cursor: "loader" }}>
        <td>
          <div className="placeholder placeholder-lg w-50"></div>
        </td>
        <td>
          <div className="d-flex align-items-center">
            <div className="placeholder rounded-circle me-2" style={{ width: 40, height: 40 }}></div>
            <div className="flex-fill">
              <div className="placeholder placeholder-lg w-75"></div>
              <div className="placeholder placeholder-sm w-50 mt-2"></div>
            </div>
          </div>
        </td>
        <td>
          <div className="placeholder placeholder-lg w-75"></div>
        </td>
        <td>
          <div className="placeholder placeholder-lg w-50"></div>
        </td>
      </tr>
    ))}
  </>
);

const EmptyState = ({ movementType }) => (
  <tr>
    <td colSpan="7" className="text-center text-secondary py-5">
      Belum ada stok material {movementType === "in" ? "masuk" : "keluar"} pada rentang tanggal ini.
    </td>
  </tr>
);

const CurrentStockSkeletonRows = ({ rows = 6 }) => (
  <>
    {[...Array(rows)].map((_, rowIndex) => (
      <tr key={`current-stock-skeleton-${rowIndex}`}>
        <td>
          <div className="placeholder placeholder-lg w-50"></div>
        </td>
        <td>
          <div className="d-flex align-items-center">
            <div className="placeholder rounded-circle me-2" style={{ width: 40, height: 40 }}></div>
            <div className="flex-fill">
              <div className="placeholder placeholder-lg w-75"></div>
            </div>
          </div>
        </td>
        {[...Array(4)].map((__, columnIndex) => (
          <td key={columnIndex}>
            <div className="placeholder placeholder-lg w-75"></div>
          </td>
        ))}
      </tr>
    ))}
  </>
);

const stockStatusMeta = {
  safe: { label: "Aman", className: "bg-success-lt" },
  critical: { label: "Kritis", className: "bg-warning-lt" },
  out_of_stock: { label: "Habis", className: "bg-danger-lt" },
};

const StockReportPage = ({
  data,
  isLoading,
  error,
  currentStock,
  currentStockLoading,
  currentStockError,
  materialConsumption,
  materialConsumptionLoading,
  materialConsumptionError,
  fetchStockMaterialUsage,
  fetchStockMaterialConsumption,
  fetchCurrentStock,
}) => {
  const [dateRange, setDateRange] = useState({ startDate: monthStart, endDate: today });
  const [movementType, setMovementType] = useState("out");
  const [search, setSearch] = useState("");
  const [stockType, setStockType] = useState("material");
  const [stockSearch, setStockSearch] = useState("");
  const [materialConsumptionFilters, setMaterialConsumptionFilters] = useState({
    category: "all",
    unit: "all",
    groupBy: "material",
    drillDownCategory: "",
  });
  const chartColors = useAkasChartColors();

  const fetchData = useCallback(() => {
    if (!dateRange.startDate || !dateRange.endDate) return;

    fetchStockMaterialUsage({
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
      movement_type: movementType,
    });
  }, [dateRange.endDate, dateRange.startDate, fetchStockMaterialUsage, movementType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchCurrentStock({ item_type: stockType });
  }, [fetchCurrentStock, stockType]);

  useEffect(() => {
    if (movementType !== "out" || !dateRange.startDate || !dateRange.endDate) return;

    fetchStockMaterialConsumption({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      ...materialConsumptionFilters,
    });
  }, [
    dateRange.endDate,
    dateRange.startDate,
    fetchStockMaterialConsumption,
    materialConsumptionFilters,
    movementType,
  ]);

  const rangeLabel = useMemo(
    () => `${formatDateLabel(dateRange.startDate)} - ${formatDateLabel(dateRange.endDate)}`,
    [dateRange.endDate, dateRange.startDate],
  );

  const handleMaterialConsumptionFilter = (name, value) => {
    setMaterialConsumptionFilters((current) => ({
      ...current,
      [name]: value,
      drillDownCategory: "",
    }));
  };

  const rows = useMemo(() => data?.data || [], [data?.data]);
  const summary = data?.summary || {};

  const filteredRows = useMemo(() => {
    const keyword = search.toLowerCase();
    if (!keyword) {
      return rows;
    }

    return rows.filter((item) => {
      return (
        item?.name?.toLowerCase().includes(keyword) ||
        item?.category?.toLowerCase().includes(keyword) ||
        item?.category_label?.toLowerCase().includes(keyword) ||
        item?.unit?.toLowerCase().includes(keyword)
      );
    });
  }, [rows, search]);

  const topMaterial = rows[0];
  const isStockIn = movementType === "in";
  const stockRows = useMemo(() => currentStock?.data || [], [currentStock?.data]);
  const stockSummary = currentStock?.summary || {};
  const filteredStockRows = useMemo(() => {
    const keyword = stockSearch.trim().toLowerCase();
    if (!keyword) return stockRows;

    return stockRows.filter((item) =>
      [item?.name, item?.category, item?.category_label, item?.type, item?.unit]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword)),
    );
  }, [stockRows, stockSearch]);

  return (
    <div className="page-wrapper">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="page-pretitle text-primary fw-bold">Stok</div>
              <h2 className="page-title">Laporan Stok</h2>
              <div className="text-secondary mt-1">
                Pantau pemakaian material, pergerakan stok, dan sisa persediaan pada rentang tanggal tertentu.
              </div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              {isLoading ? (
                <div className="placeholder-glow">
                  <div className="placeholder rounded-3" style={{ width: 180, height: 38 }}></div>
                </div>
              ) : (
                <div className="badge bg-primary-lt fs-3 px-3 py-2">
                  {`${formatNumber(summary?.total_transactions)} sumber stok ${isStockIn ? "masuk" : "keluar"}`}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-3">
                  <label className="form-label">Tanggal Awal</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.startDate}
                    onChange={(event) => setDateRange({ ...dateRange, startDate: event.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Tanggal Akhir</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateRange.endDate}
                    onChange={(event) => setDateRange({ ...dateRange, endDate: event.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Pergerakan Stok</label>
                  <div className="btn-group w-100 gap-2" role="group" aria-label="Filter pergerakan stok material">
                    <button
                      type="button"
                      className={`btn ${movementType === "out" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setMovementType("out")}>
                      Stok Keluar
                    </button>
                    <button
                      type="button"
                      className={`btn ${movementType === "in" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setMovementType("in")}>
                      Stok Masuk
                    </button>
                  </div>
                </div>
                <div className="col-md-3">
                  <button className="btn btn-primary w-100" type="button" onClick={fetchData} disabled={isLoading}>
                    {isLoading ? "Memuat..." : "Terapkan Filter"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {movementType === "out" ? (
            <div className="mb-3">
              <MaterialUsageChart
                data={materialConsumption}
                loading={materialConsumptionLoading}
                error={materialConsumptionError}
                filters={materialConsumptionFilters}
                rangeLabel={rangeLabel}
                chartColors={chartColors}
                onFilterChange={handleMaterialConsumptionFilter}
                onDrillDown={(category) => setMaterialConsumptionFilters((current) => ({
                  ...current,
                  drillDownCategory: category,
                }))}
                onClearDrillDown={() => setMaterialConsumptionFilters((current) => ({
                  ...current,
                  drillDownCategory: "",
                }))}
              />
            </div>
          ) : null}

          <div className="row g-3 mb-3">
            <SummaryCard
              title={isStockIn ? "Material Masuk" : "Material Keluar"}
              value={formatNumber(summary?.total_materials)}
              subtitle={`jenis material dengan mutasi ${isStockIn ? "masuk" : "keluar"}`}
            />
            <SummaryCard
              title="Sumber Mutasi"
              value={formatNumber(summary?.total_transactions)}
              subtitle={isStockIn ? "pembelian/proses penambah stok" : "produksi/proses pengurang stok"}
              color="success"
            />
            <SummaryCard
              title="Jumlah Mutasi"
              value={formatNumber(summary?.total_movements)}
              subtitle={`catatan ledger stok ${isStockIn ? "masuk" : "keluar"}`}
              color="info"
            />
            <SummaryCard
              title="Terbesar"
              value={
                topMaterial ? formatQty(topMaterial?.movement_qty ?? topMaterial?.used_qty, topMaterial?.unit) : "-"
              }
              subtitle={topMaterial?.name || "belum ada material"}
              color="warning"
            />
          </div>

          <div className="card">
            <div className="card-table">
              <div className="card-header">
                <div className="row w-full align-items-center g-2">
                  <div className="col-md-7 col-12">
                    <h3 className="card-title mb-0">Tabel Material</h3>
                    {isLoading ? (
                      <div className="placeholder-glow mt-2">
                        <div
                          className="placeholder rounded-2"
                          style={{ width: 300, maxWidth: "100%", height: 12 }}></div>
                      </div>
                    ) : (
                      <p className="text-secondary m-0">
                        Menampilkan material dengan stok {isStockIn ? "masuk" : "keluar"} pada periode terpilih.
                      </p>
                    )}
                  </div>
                  <div className="col-md-5 col-12 my-md-0 my-2">
                    <div className="input-group input-group-flat w-auto">
                      {isLoading ? (
                        <span className="input-group-text">
                          <div
                            className="spinner-border spinner-border-sm text-primary text-center"
                            role="status"></div>
                        </span>
                      ) : (
                        <span className="input-group-text">
                          <StockReportIcon name="search" />
                        </span>
                      )}
                      <input
                        type="text"
                        className="form-control"
                        autoComplete="off"
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Cari material, kategori, satuan..."
                        value={search}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-vcenter table-selectable">
                  <thead className="table-header">
                    <tr>
                      <th>No.</th>
                      <th>Material</th>
                      <th>Kategori</th>
                      {/* <th>Satuan</th> */}
                      <th>{isStockIn ? "Stok Masuk" : "Stok Keluar"}</th>
                      {/* <th className="text-end">Sisa Stok</th>
                      <th className="text-end">Sumber Mutasi</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <TableSkeletonRows rows={3} />
                    ) : filteredRows.length ? (
                      filteredRows.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}.</td>
                          <td>
                            <div className="d-flex py-1 align-items-center">
                              <ItemAvatar
                                name={item?.name}
                                image={item?.image_path || item?.image}
                                size="md"
                                className="me-2"
                              />
                              <div className="flex-fill">
                                <div className="font-weight-medium">{item?.name}</div>
                                <div className="text-secondary small">ID #{item?.id}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="badge bg-primary-lt text-primary">
                              {item?.category_label || item?.category || "-"}
                            </span>
                          </td>
                          {/* <td className="text-capitalize">{item?.unit || "-"}</td> */}
                          <td
                            className={`text-start fw-bold ${movementType === "out" ? "text-danger" : "text-success"}`}>
                            {formatQty(item?.movement_qty ?? item?.used_qty, item?.unit)}

                            {movementType === "out" ? (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="icon icon-tabler icons-tabler-outline icon-tabler-arrow-narrow-down text-danger">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M12 5l0 14" />
                                <path d="M16 15l-4 4" />
                                <path d="M8 15l4 4" />
                              </svg>
                            ) : (
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="icon icon-tabler icons-tabler-outline icon-tabler-arrow-narrow-up text-success">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M12 5l0 14" />
                                <path d="M16 9l-4 -4" />
                                <path d="M8 9l4 -4" />
                              </svg>
                            )}
                          </td>
                          {/* <td className="text-end fw-bold">{formatQty(item?.stock, item?.unit)}</td>
                          <td className="text-end">{formatNumber(item?.transaction_count)}</td> */}
                        </tr>
                      ))
                    ) : (
                      <EmptyState movementType={movementType} />
                    )}
                  </tbody>
                </table>
              </div>
              <div className="card-footer d-flex align-items-center">
                {isLoading ? (
                  <div className="placeholder-glow w-100">
                    <div className="placeholder rounded-2" style={{ width: 230, height: 12 }}></div>
                  </div>
                ) : (
                  <p className="m-0 text-secondary">
                    Menampilkan <span>{filteredRows.length}</span> dari <span>{rows.length}</span> material
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="card mt-3">
            <div className="card-table">
              <div className="card-header">
                <div className="row w-full align-items-center">
                  <div className="col-lg-6 col-12">
                    <h3 className="card-title mb-0">Sisa Stok Saat Ini</h3>
                    {currentStockLoading ? (
                      <div className="placeholder-glow mt-2">
                        <div
                          className="placeholder rounded-2"
                          style={{ width: 300, maxWidth: "100%", height: 12 }}></div>
                      </div>
                    ) : (
                      <p className="text-secondary m-0">
                        {formatNumber(stockSummary.total_items)} item · {formatNumber(stockSummary.critical_items)}{" "}
                        kritis · {formatNumber(stockSummary.out_of_stock_items)} habis
                      </p>
                    )}
                  </div>
                  <div className="col-lg-3 col-md-5 col-12 my-lg-0 my-2">
                    <AkasSelect
                      className="form-select"
                      value={stockType}
                      onChange={(event) => setStockType(event.target.value)}
                      disabled={currentStockLoading}>
                      <option value="material">Material</option>
                      <option value="product">Produk</option>
                      <option value="all">Semua</option>
                    </AkasSelect>
                  </div>
                  <div className="col-lg-3 col-md-7 col-12 my-lg-0 my-2">
                    <div className="input-group input-group-flat w-auto">
                      <span className="input-group-text">
                        {currentStockLoading ? (
                          <span className="spinner-border spinner-border-sm text-primary" role="status"></span>
                        ) : (
                          <StockReportIcon name="search" />
                        )}
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        value={stockSearch}
                        onChange={(event) => setStockSearch(event.target.value)}
                        placeholder="Cari..."
                        autoComplete="off"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {currentStockError ? <div className="alert alert-danger m-3 mb-0">{currentStockError}</div> : null}

              <div className="table-responsive">
                <table className="table table-vcenter table-selectable">
                  <thead className="table-header">
                    <tr>
                      <th className="sort-name py-3 w-1">No.</th>
                      <th className="fw-bold">Item</th>
                      <th className="fw-bold">Jenis</th>
                      <th className="fw-bold">Kategori</th>
                      {/* <th className="fw-bold">Satuan Stok</th>
                      <th className="fw-bold text-end">Minimum</th> */}
                      <th className="fw-bold text-end">Sisa Stok</th>
                      <th className="fw-bold text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="table-tbody">
                    {currentStockLoading ? (
                      <CurrentStockSkeletonRows />
                    ) : filteredStockRows.length ? (
                      filteredStockRows.map((item, index) => {
                        const status = stockStatusMeta[item?.stock_status] || stockStatusMeta.safe;

                        return (
                          <tr key={`${item.item_type}-${item.id}`}>
                            <td>{index + 1}.</td>
                            <td>
                              <div className="d-flex py-1 align-items-center">
                                <ItemAvatar
                                  name={item?.name}
                                  image={item?.image_path || item?.image}
                                  size="md"
                                  className="me-2"
                                />
                                <div className="flex-fill">
                                  <div className="font-weight-medium">{item?.name}</div>
                                  <div className="text-secondary small text-capitalize">{item?.type || "-"}</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <span
                                className={`badge ${item?.item_type === "material" ? "bg-primary-lt" : "bg-info-lt"}`}>
                                {item?.item_type === "material" ? "Material" : "Produk"}
                              </span>
                            </td>
                            <td>{item?.category_label || item?.category || "-"}</td>
                            {/* <td className="text-capitalize">{item?.stock_unit || item?.unit || "-"}</td>
                            <td className="text-end text-secondary">
                              {formatQty(item?.min_stock, item?.stock_unit || item?.unit)}
                            </td> */}
                            <td className="text-end fw-bold">
                              {formatQty(item?.stock, item?.stock_unit || item?.unit)}
                            </td>
                            <td className="text-center">
                              <span className={`badge ${status.className}`}>{status.label}</span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center text-secondary py-5">
                          Tidak ada data stok yang sesuai dengan filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="card-footer d-flex align-items-center">
                {currentStockLoading ? (
                  <div className="placeholder placeholder-lg" style={{ width: 220 }}></div>
                ) : (
                  <p className="m-0 text-secondary">
                    Menampilkan <span>{filteredStockRows.length}</span> dari <span>{stockRows.length}</span> item stok
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  data: state?.stockReports?.datas,
  isLoading: state?.stockReports?.loading || false,
  error: state?.stockReports?.error,
  currentStock: state?.stockReports?.currentStock,
  currentStockLoading: state?.stockReports?.currentStockLoading || false,
  currentStockError: state?.stockReports?.currentStockError,
  materialConsumption: state?.stockReports?.materialConsumption,
  materialConsumptionLoading: state?.stockReports?.materialConsumptionLoading || false,
  materialConsumptionError: state?.stockReports?.materialConsumptionError,
});

export default connect(mapStateToProps, {
  fetchStockMaterialUsage,
  fetchStockMaterialConsumption,
  fetchCurrentStock,
})(StockReportPage);
